import { createSerializer, parseAsStringLiteral } from "nuqs/server";
import type {
  BootstrapJob,
  BusinessContextForm,
  OnboardingSession,
} from "./graphql";

export const emptyContextForm: BusinessContextForm = {
  businessName: "",
  businessType: "",
  industry: "",
  locations: "",
  primaryProductsServices: "",
};

const canonicalSearchAnalyticsOnboardingStepSlugs = [
  "site",
  "business",
  "analysis",
  "results",
] as const;

const searchAnalyticsOnboardingStepSlugs = [
  ...canonicalSearchAnalyticsOnboardingStepSlugs,
  "search-console",
] as const;

export type SearchAnalyticsOnboardingStepSlug =
  (typeof searchAnalyticsOnboardingStepSlugs)[number];

export const searchAnalyticsOnboardingStepParser = parseAsStringLiteral(
  searchAnalyticsOnboardingStepSlugs,
);

/**
 * SA onboarding の遷移先を、許可済み `step` slug の query として生成します。
 * default step は補わず、`null` は省略して、未対応 slug は型 contract で受け付けません。
 */
export const serializeSearchAnalyticsOnboardingSearchParams = createSerializer({
  step: searchAnalyticsOnboardingStepParser,
});

/**
 * Session と最新 job から、ユーザーが次に完了すべき wizard step を復元します。
 * Backend status は完了 mutation が未実装なため、入力済み field と snapshot 有無を優先します。
 * step 3 は分析条件入力、step 4 は実行中/結果確認に分け、URL と画面責務を一致させます。
 */
export function getActiveStep(
  session: OnboardingSession | null,
  job: BootstrapJob | null,
) {
  if (!session) {
    return 0;
  }
  if (!session.siteURL) {
    return 0;
  }
  if (!session.confirmedBusinessContext) {
    return 1;
  }
  if (!session.goal) {
    return 2;
  }
  if (job || session.latestSnapshotID) {
    return 3;
  }
  return 2;
}

/**
 * site step で既存 session の property 選択 UI を再利用してよいかを判定します。
 * APPLIED 済み session は直接編集せず、start mutation 側の current session restart に任せます。
 */
export function siteStepCanEditExistingSessionForCurrentURL({
  currentSiteURL,
  session,
}: {
  currentSiteURL: string;
  session: Pick<OnboardingSession, "siteURL" | "status"> | null;
}) {
  if (!session?.siteURL || session.status === "APPLIED") {
    return false;
  }
  const normalizedCurrentSiteURL = normalizeSiteURL(currentSiteURL);
  return Boolean(
    normalizedCurrentSiteURL &&
      normalizeSiteURL(session.siteURL) === normalizedCurrentSiteURL,
  );
}

/**
 * URL に保持する初期分析 step 名を、表示用 step index から取得します。
 * 範囲外の値は wizard が実際に表示できる 0-3 の範囲へ丸めます。
 */
export function searchAnalyticsOnboardingStepSlug(
  stepIndex: number,
): (typeof canonicalSearchAnalyticsOnboardingStepSlugs)[number] {
  const index = Math.min(
    canonicalSearchAnalyticsOnboardingStepSlugs.length - 1,
    Math.max(0, Math.trunc(stepIndex)),
  );
  return canonicalSearchAnalyticsOnboardingStepSlugs[index] ?? "site";
}

/**
 * URL の `step` slug を wizard の表示用 step index に変換します。
 * `nuqs` parser 通過後の正規化済み slug だけを受け取り、URL と表示 step の対応を一箇所に閉じます。
 */
export function searchAnalyticsOnboardingStepIndex(
  value: SearchAnalyticsOnboardingStepSlug,
) {
  if (value === "search-console") {
    return 0;
  }
  return canonicalSearchAnalyticsOnboardingStepSlugs.indexOf(value);
}

/**
 * URL の `step` 値を wizard の表示用 step index に変換します。
 * 不明な値は null にし、server component 側や pure test で安全な fallback を選べるようにします。
 */
export function searchAnalyticsOnboardingStepIndexFromURLValue(
  value: string | null,
) {
  if (!value) {
    return null;
  }
  const parsed = searchAnalyticsOnboardingStepParser.parse(value);
  return parsed ? searchAnalyticsOnboardingStepIndex(parsed) : null;
}

export function toContextForm(
  context: NonNullable<OnboardingSession["draftBusinessContext"]>,
): BusinessContextForm {
  return {
    businessName: context.businessName ?? "",
    businessType: context.businessType ?? "",
    industry: context.industry ?? "",
    locations: context.locations.join(", "),
    primaryProductsServices: context.primaryProductsServices.join("\n"),
  };
}

type BusinessContextLike = {
  businessName?: string | null;
  businessType?: string | null;
  industry?: string | null;
  locations?: readonly (string | null)[] | null;
  primaryProductsServices?: readonly (string | null)[] | null;
} | null;

export function hasMeaningfulBusinessContext(
  context: BusinessContextLike | undefined,
) {
  if (!context) {
    return false;
  }
  return [
    context.businessName,
    context.businessType,
    context.industry,
    ...(context.locations ?? []),
    ...(context.primaryProductsServices ?? []),
  ].some((value) => Boolean(value?.trim()));
}

export function toBusinessContextInput(form: BusinessContextForm) {
  return {
    businessName: form.businessName || null,
    businessType: form.businessType || null,
    industry: form.industry || null,
    locations: splitList(form.locations),
    primaryProductsServices: splitList(form.primaryProductsServices).slice(
      0,
      5,
    ),
  };
}

export function normalizeSiteURL(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    return new URL(withProtocol).href;
  } catch {
    return null;
  }
}

export type GSCPropertyKind = "domain" | "url_prefix" | "unknown";

export type GSCPropertyOption = {
  property: string;
  optionValue?: string;
  coversSiteURL: boolean;
  ownerEmail?: string | null;
  ownerType?: "USER" | "SERVICE_ACCOUNT";
  serviceAccountID?: string | null;
};

/**
 * 同じ Search Console property を user OAuth と service account credential で区別するための Select 値を返します。
 * optionValue を持たない入力では property そのものを代替値にします。
 */
export function searchConsolePropertyOptionValue(
  property: Pick<
    GSCPropertyOption,
    "property" | "optionValue" | "ownerType" | "serviceAccountID"
  >,
) {
  if (property.optionValue) {
    return property.optionValue;
  }
  if (property.ownerType) {
    return `${property.property}___${property.ownerType}___${property.serviceAccountID ?? ""}`;
  }
  return property.property;
}

/**
 * Search Console property の種類を判定し、選択 UI で domain / URL prefix の違いを説明できるようにします。
 */
export function classifyGSCProperty(property: string): GSCPropertyKind {
  if (property.startsWith("sc-domain:")) {
    return "domain";
  }

  try {
    new URL(property);
    return "url_prefix";
  } catch {
    return "unknown";
  }
}

/**
 * 入力サイトが Search Console property の対象範囲に含まれるかを判定します。
 * Domain property はサブドメインを含め、URL prefix property は prefix が一致する URL だけを対象にします。
 */
export function propertyCoversURL(property: string, siteURL: string) {
  const normalizedURL = toGSCPropertyCoverageURL(siteURL);
  if (!normalizedURL) {
    return false;
  }
  const site = new URL(normalizedURL);
  const hostname = site.hostname.toLowerCase().replace(/^www\./, "");
  const propertyForComparison = property.trim();
  const lowerCasePropertyForDomainComparison =
    propertyForComparison.toLowerCase();

  if (lowerCasePropertyForDomainComparison.startsWith("sc-domain:")) {
    const domain = lowerCasePropertyForDomainComparison
      .replace("sc-domain:", "")
      .replace(/^www\./, "");
    return hostname === domain || hostname.endsWith(`.${domain}`);
  }

  const normalizedProperty = toGSCPropertyCoverageURL(propertyForComparison);
  if (!normalizedProperty) {
    return false;
  }

  return `${normalizedURL}/`.startsWith(`${normalizedProperty}/`);
}

/**
 * Search Console property を入力サイトとの一致度順に並べ、非一致 property も確認用に残します。
 * ユーザーが別 property の存在を把握できるよう、選択可能候補を隠さずに優先度だけ下げます。
 */
export function listGSCPropertiesBySiteCoverage<
  TProperty extends { property: string },
>(
  properties: ReadonlyArray<TProperty>,
  siteURL: string,
): Array<TProperty & { coversSiteURL: boolean }> {
  return properties
    .map((property) => ({
      ...property,
      coversSiteURL: propertyCoversURL(property.property, siteURL),
    }))
    .sort((a, b) => {
      if (a.coversSiteURL !== b.coversSiteURL) {
        return a.coversSiteURL ? -1 : 1;
      }
      return a.property.localeCompare(b.property);
    });
}

/**
 * 現在の選択が分析対象 URL をカバーしない場合、先頭の一致 property を初期選択にします。
 * 非一致 property は確認用に表示するだけで、初期分析には使わせないようにします。
 */
export function chooseMatchingGSCPropertyForOnboarding({
  currentOptionValue,
  properties,
}: {
  currentOptionValue: string;
  properties: ReadonlyArray<GSCPropertyOption>;
}) {
  const currentOption = properties.find(
    (property) =>
      searchConsolePropertyOptionValue(property) === currentOptionValue,
  );
  if (currentOption?.coversSiteURL) {
    return searchConsolePropertyOptionValue(currentOption);
  }

  const matchingOption = properties.find((property) => property.coversSiteURL);
  return matchingOption ? searchConsolePropertyOptionValue(matchingOption) : "";
}

/**
 * 対象サイト分析の開始操作で、同じクリックのまま保存してよい Search Console property を返します。
 * 既存 session の stale property を再利用せず、現在の入力 URL を実際にカバーする選択だけを後続 mutation に渡します。
 */
export function coveredSearchConsolePropertyForSiteAnalysisStart({
  selectedProperty,
  siteURL,
}: {
  selectedProperty: string;
  siteURL: string;
}) {
  if (!selectedProperty) {
    return "";
  }
  return propertyCoversURL(selectedProperty, siteURL) ? selectedProperty : "";
}

/**
 * mutation 直後に保持した bootstrap job を、polling query の結果で置き換えてよいかを判定します。
 * Graphcache が古い完了状態を一瞬返すことがあるため、同一 job の status 差分は updatedAt が mutation 以上のときだけ採用します。
 */
export function queriedBootstrapJobShouldReplaceMutation({
  mutationJob,
  queryJob,
}: {
  mutationJob: BootstrapJob;
  queryJob: BootstrapJob;
}) {
  if (queryJob.projectID !== mutationJob.projectID) {
    return true;
  }
  if (queryJob.status === mutationJob.status) {
    return true;
  }

  const mutationUpdatedAt = Date.parse(mutationJob.updatedAt);
  const queryUpdatedAt = Date.parse(queryJob.updatedAt);
  if (!Number.isFinite(mutationUpdatedAt) || !Number.isFinite(queryUpdatedAt)) {
    return false;
  }

  return queryUpdatedAt >= mutationUpdatedAt;
}

export function isFinalJobStatus(status: BootstrapJob["status"] | undefined) {
  return status === "PREVIEW_READY" || status === "FAILED";
}

export function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value);
}

function splitList(value: string) {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Backend の GSCPropertyCoversURL と同じ比較形に寄せ、URL prefix の path casing を保ったまま兄弟 path を除外します。
 */
function toGSCPropertyCoverageURL(value: string) {
  const normalizedURL = normalizeSiteURL(value);
  if (!normalizedURL) {
    return null;
  }

  try {
    const url = new URL(normalizedURL);
    url.hash = "";
    url.search = "";
    return url.href.replace(/\/+$/, "");
  } catch {
    return null;
  }
}
